import { useMemo } from "react";
import { UnitTemplateOption } from "types/landlord/address";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { useQuery } from "@tanstack/react-query";
import addressApi from "api/landlord/address";
import { TemplateLinksMenuTrigger } from "./TemplateLinksMenuTrigger";
import { TemplateLinksSubmenuVariant, TemplateLink } from "./types";

interface TemplateLinksSubmenuProps {
  disabled?: boolean;
  onCopyLink: (link: string) => void;
  defaultLink: string;
  unitId?: string;
  variant?: TemplateLinksSubmenuVariant;
}

export const TemplateLinksSubmenu = (props: TemplateLinksSubmenuProps) => {
  const variant = props.variant || "FULL";
  const { data: templateOptions = [], isLoading } = useQuery<
    UnitTemplateOption[]
  >({
    queryKey: ["UNITS", props.unitId, "TEMPLATE_OPTIONS"],
    queryFn: async () => {
      const response = await addressApi.getUnitTemplateOptions(
        String(props.unitId)
      );
      return response.items;
    },
    enabled: Boolean(props.unitId)
  });

  const templateLinks = useMemo<TemplateLink[]>(() => {
    const links: TemplateLink[] = [];

    if (props.defaultLink)
      links.push({
        id: "default",
        name: "Default template",
        link: props.defaultLink
      });

    if (templateOptions.length > 0)
      templateOptions.forEach((option) => {
        links.push({
          id: option.slug || String(option.id),
          name: option.name,
          link: option.magic_link.application_link
        });
      });

    return links;
  }, [props.defaultLink, templateOptions]);

  if (isLoading) return null;

  return (
    <ContextMenuPopover
      placement={variant == "BUTTON" ? "bottom-end" : "left-start"}
      disabled={props.disabled || isLoading}
      contentClassName={
        variant === "BUTTON" ? "w-[265px]" : "[&>div]:!-translate-x-4 w-[265px]"
      }
      cbTrigger={() => (
        <TemplateLinksMenuTrigger variant={variant} disabled={props.disabled} />
      )}
      actions={templateLinks.map((template) => ({
        title: (
          <h2 className="py-1 text-base font-medium text-content-1000">
            {template.name}
          </h2>
        ),
        onClick: (e) => {
          e.stopPropagation();
          props.onCopyLink(template.link);
        }
      }))}
    />
  );
};
